import React from 'react';
import {
  Switch,
  FormControlLabel,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
} from '@mui/material';
import WarningAmberOutlinedIcon from '@mui/icons-material/WarningAmberOutlined';
import InputField from '../../../../components/inputfield/InputField';
import styles from './FiscalSettings.module.css';

/**
 * Seção "Configurações de Serviço (NFS-e)" da tela Configurações > Nota Fiscal.
 *
 * Blocos: forma de emissão (layout + enquadramento), serviço do layout escolhido (Emissor
 * Nacional ou prefeitura), numeração da DPS (só nacional) e Reforma Tributária (IBS/CBS, os dois
 * layouts). Rótulo curto, no máximo uma linha de ajuda por campo; contexto fica no título do bloco.
 *
 * Backend: get_fiscal_settings.php / set_fiscal_settings.php; emissão em FocusNfeProvider.php
 * (municipal) e FocusNfsenPayloadBuilder.php (nacional).
 */

export const NFSE_LAYOUTS = [
  { value: 'municipal', label: 'Padrão da prefeitura' },
  { value: 'nacional', label: 'Emissor Nacional NFS-e' },
];

export const OPCOES_SIMPLES_NACIONAL = [
  { value: 1, label: 'Não optante pelo Simples Nacional' },
  { value: 2, label: 'MEI' },
  { value: 3, label: 'ME ou EPP optante pelo Simples Nacional' },
];

const RECOLHIMENTO_ISS_SN = [
  { value: '', label: 'Não informar' },
  { value: 1, label: 'Pelo Simples Nacional' },
  { value: 2, label: 'Fora do Simples, só o ISS' },
  { value: 3, label: 'Fora do Simples, todos os tributos' },
];

const SITUACOES_ISS = [
  { value: 1, label: 'Operação tributável' },
  { value: 2, label: 'Imunidade' },
  { value: 3, label: 'Exportação de serviço' },
  { value: 4, label: 'Não incidência' },
];

const REGIMES_ESPECIAIS = [
  { value: 0, label: 'Nenhum' },
  { value: 1, label: 'Ato cooperado' },
  { value: 2, label: 'Estimativa' },
  { value: 3, label: 'Microempresa municipal' },
  { value: 4, label: 'Notário ou registrador' },
  { value: 5, label: 'Profissional autônomo' },
  { value: 6, label: 'Sociedade de profissionais' },
  { value: 9, label: 'Outros' },
];

const NATUREZAS_OPERACAO = [
  { value: '1', label: 'Tributação no município' },
  { value: '2', label: 'Tributação fora do município' },
  { value: '3', label: 'Isenção' },
  { value: '4', label: 'Imune' },
  { value: '5', label: 'Exigibilidade suspensa por decisão judicial' },
  { value: '6', label: 'Exigibilidade suspensa por procedimento administrativo' },
];

const SelectField = ({ label, value, options, onChange, disabled, hint }) => (
  <div className={styles.formGroup}>
    <FormControl size="small" disabled={disabled} fullWidth>
      <InputLabel shrink>{label}</InputLabel>
      <Select value={value} label={label} onChange={onChange} displayEmpty notched>
        {options.map((opt) => (
          <MenuItem key={String(opt.value)} value={opt.value}>{opt.label}</MenuItem>
        ))}
      </Select>
    </FormControl>
    {hint && <span className={styles.apiKeyHint}>{hint}</span>}
  </div>
);

const InputWithHint = ({ label, field, form, errors, editing, onFieldChange, placeholder, type, hint }) => (
  <div className={styles.formGroup}>
    <InputField
      Label={label}
      Value={form[field]}
      OnUpdateValue={onFieldChange(field)}
      Disabled={!editing}
      placeholder={placeholder}
      type={type}
    />
    {errors[field] && <span className={styles.fieldError}>{errors[field]}</span>}
    {hint && <span className={styles.apiKeyHint}>{hint}</span>}
  </div>
);

const Block = ({ title, hint, children }) => (
  <div className={styles.subsection}>
    <div className={styles.subsectionTitle}>{title}</div>
    {hint && <div className={styles.subsectionHint}>{hint}</div>}
    <div className={styles.formGrid}>{children}</div>
  </div>
);

const FiscalNfseLayoutSection = ({ form, errors = {}, editing, onFieldChange, onSwitchChange, onSelectChange }) => {
  const isNacional = form.nfse_layout === 'nacional';
  const opcao = Number(form.codigo_opcao_simples_nacional);
  const textProps = { form, errors, editing, onFieldChange };
  // Resolução CGSN 191/2026: MEI e ME/EPP do Simples emitem só pelo Emissor Nacional desde 01/11/2026.
  const simplesNoMunicipal = !isNacional && opcao !== 1;

  const ibsCbsHint = () => {
    if (isNacional && opcao === 1) return 'Não optante: enviados em todas as notas.';
    if (isNacional) return 'No Simples Nacional, enviados só com a chave abaixo ligada. Obrigatório a partir de 01/01/2027.';
    return 'No padrão da prefeitura de São Paulo, enviados só para não optantes pelo Simples.';
  };

  return (
    <div className={styles.section}>
      <div className={styles.sectionTitle}>Configurações de Serviço (NFS-e)</div>

      <Block title="Forma de emissão">
        <SelectField
          label="Layout de emissão"
          value={form.nfse_layout}
          options={NFSE_LAYOUTS}
          onChange={onSelectChange('nfse_layout')}
          disabled={!editing}
          hint={isNacional
            ? 'Obrigatório para o Simples Nacional a partir de 01/11/2026.'
            : 'Fluxo atual, pelo padrão da sua prefeitura.'}
        />
        <SelectField
          label="Enquadramento tributário"
          value={opcao}
          options={OPCOES_SIMPLES_NACIONAL}
          onChange={onSelectChange('codigo_opcao_simples_nacional')}
          disabled={!editing}
        />
        {opcao === 3 && (
          <SelectField
            label="Recolhimento do ISS"
            value={form.regime_tributario_simples_nacional ?? ''}
            options={RECOLHIMENTO_ISS_SN}
            onChange={onSelectChange('regime_tributario_simples_nacional')}
            disabled={!editing}
            hint="Fora do Simples, a alíquota padrão vai na nota."
          />
        )}
      </Block>

      {simplesNoMunicipal && (
        <Alert severity="warning" icon={<WarningAmberOutlinedIcon fontSize="small" />}>
          <strong>No Simples Nacional, a nota sai pelo Emissor Nacional desde 01/11/2026.</strong>{' '}
          Troque o layout para Emissor Nacional NFS-e e, no painel da Focus NFe, ligue "Ambiente da NFSe
          Nacional" e desligue "NFSe" no mesmo momento.
        </Alert>
      )}

      {isNacional && (
        <Block
          title="Serviço no Emissor Nacional"
          hint="Mesmos códigos do perfil de serviço cadastrado no Emissor Nacional. Confirme com a contabilidade."
        >
          <InputWithHint
            label="Código de tributação nacional"
            field="codigo_tributacao_nacional_iss"
            placeholder="040802"
            hint="6 dígitos, sem pontos. Ex.: 040802"
            {...textProps}
          />
          <SelectField
            label="Situação do ISS"
            value={Number(form.tributacao_iss)}
            options={SITUACOES_ISS}
            onChange={onSelectChange('tributacao_iss')}
            disabled={!editing}
          />
          <SelectField
            label="Regime especial"
            value={Number(form.regime_especial_tributacao)}
            options={REGIMES_ESPECIAIS}
            onChange={onSelectChange('regime_especial_tributacao')}
            disabled={!editing}
          />
          {opcao === 3 && (
            <InputWithHint
              label="Tributos aproximados do Simples (%)"
              field="percentual_tributos_simples_nacional"
              placeholder="6,00"
              hint="Percentual aproximado do Simples sobre a nota. Confirme com a contabilidade."
              {...textProps}
            />
          )}
        </Block>
      )}

      {isNacional && (
        <Block
          title="Numeração da DPS"
          hint="Cada emissão consome um número, mesmo rejeitada. Se já emitiu por outro sistema, informe o próximo número livre."
        >
          <InputWithHint label="Série" field="dps_serie" type="number" placeholder="1" {...textProps} />
          <InputWithHint label="Próximo número" field="dps_proximo_numero" type="number" placeholder="1" {...textProps} />
        </Block>
      )}

      {!isNacional && (
        <Block title="Serviço na prefeitura">
          <div className={styles.formGroup}>
            <InputField
              Label="Item Lista Serviço"
              Value={form.item_lista_servico}
              OnUpdateValue={onFieldChange('item_lista_servico')}
              Disabled={!editing}
              placeholder="043901"
            />
            <span className={styles.apiKeyHint}>
              6 dígitos da tabela da prefeitura. Ex.: 043901 (Fisioterapia, SP).{' '}
              <a href="https://focusnfe.com.br/guides/nfse/municipios-integrados/sao-paulo-sp/" target="_blank" rel="noopener noreferrer">
                Lista oficial
              </a>
            </span>
          </div>
          <SelectField
            label="Natureza da operação"
            value={form.natureza_operacao}
            options={NATUREZAS_OPERACAO}
            onChange={onSelectChange('natureza_operacao')}
            disabled={!editing}
          />
        </Block>
      )}

      <Block title="Reforma Tributária (IBS e CBS)" hint={ibsCbsHint()}>
        {isNacional && opcao !== 1 && (
          <div className={`${styles.formGroup} ${styles.formGridFull}`}>
            <FormControlLabel
              control={
                <Switch
                  checked={!!form.ibs_cbs_informar}
                  onChange={onSwitchChange('ibs_cbs_informar')}
                  color="primary"
                  disabled={!editing}
                />
              }
              label="Informar IBS e CBS na nota"
            />
          </div>
        )}
        <InputWithHint label="Código NBS" field="codigo_nbs" placeholder="000000000" hint="Ex.: 123019200" {...textProps} />
        <InputWithHint
          label="Classificação tributária"
          field="ibs_cbs_classificacao_tributaria"
          placeholder="000001"
          hint="Ex.: 200029"
          {...textProps}
        />
        <InputWithHint
          label="Indicador de operação"
          field="codigo_indicador_operacao"
          placeholder="000000"
          hint="Ex.: 100301"
          {...textProps}
        />
      </Block>
    </div>
  );
};

export default FiscalNfseLayoutSection;
